import { ReactNode, useCallback, useEffect } from 'react';
import Spinner from '../spinner/Spinner';
import { TooltipV2 } from '../tooltip/TooltipV2';

const styles = {
    base: 'focus:outline-none focus:ring-0 disabled:cursor-default disabled:opacity-50',
    type: {
        primary:
            'border-transparent border shadow-sm font-medium text-white dark:text-neutral-50 bg-indigo-600 dark:bg-indigo-500 hover:bg-indigo-700 dark:hover:bg-indigo-600 active:bg-indigo-800 dark:active:bg-indigo-700',
        secondary:
            'border border-gray-300 dark:border-white/[0.05] dark:hover:border-white/[0.07] dark:active:border-white/[0.11] shadow-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-white/[0.05] hover:bg-gray-50 dark:hover:bg-white/[0.09] active:bg-gray-100 dark:active:bg-white/[0.3] dark:disabled:bg-white/[0.1]',
        hollow: 'border border-transparent font-medium text-gray-700 dark:text-gray-200 dark:bg-white/[0.0] hover:bg-gray-50 dark:hover:bg-white/[0.09] active:bg-gray-100 dark:active:bg-white/[0.11]',
        danger: 'border border-transparent shadow-sm font-medium text-white dark:text-neutral-50 bg-red-600 dark:bg-red-500 hover:bg-red-700 dark:hover:bg-red-600 active:bg-red-800 dark:active:bg-red-700',
    },
    size: {
        xs: 'px-2.5 py-2 text-xs',
        sm: 'px-2.5 py-2 text-sm',
        base: 'px-2.5 py-2 text-base',
        lg: 'px-2.5 py-2 text-lg',
        xl: 'px-2.5 py-2 text-xl',
    },
    iconDimension: {
        xs: 'h-3 w-3',
        sm: 'h-3.5 w-3.5',
        base: 'h-4 w-4',
        lg: 'h-5 w-5',
        xl: 'h-6 w-6',
    },
    iconColour: {
        primary: 'text-white',
        secondary: 'text-indigo-600 dark:text-indigo-400',
        hollow: 'text-indigo-600 dark:text-indigo-400',
        danger: 'text-white',
    },
    shortcutKey: {
        primary:
            'text-white bg-indigo-500 group-hover:bg-indigo-600 group-active:bg-indigo-700 dark:bg-indigo-600 dark:group-hover:bg-indigo-700 dark:group-active:bg-indigo-800',
        secondary:
            'text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-300/[0.1] group-hover:bg-gray-200 dark:group-hover:bg-gray-300/[0.1] group-active:bg-gray-300 dark:group-active:bg-gray-300/[0.1]',
        hollow: 'text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-300/[0.1] group-hover:bg-gray-200 dark:group-hover:bg-gray-300/[0.1] group-active:bg-gray-300 dark:group-active:bg-gray-300/[0.1]',
        danger: 'text-white bg-red-500 dark:bg-red-600 group-hover:bg-red-600 dark:group-hover:bg-red-700 group-active:bg-red-700 dark:group-active:bg-red-800',
    },

    shortcutKeySize: {
        xs: 'text-mini',
        sm: 'text-xs',
        base: 'text-sm',
        lg: 'text-base',
        xl: 'text-lg',
    },
};

export type ButtonV2Props = {
    type?: 'primary' | 'danger' | 'secondary' | 'hollow';
    size?: 'xs' | 'sm' | 'base' | 'lg' | 'xl';
    label?: string;
    leftIcon?: ReactNode;
    rightIcon?: ReactNode;
    isDisabled?: boolean;
    disabledHint?: string | string[];
    tooltipPosition?: 'top' | 'right' | 'bottom' | 'left';
    isLoading?: boolean;
    isSubmit?: boolean;
    shortcutKey?: string;
    requiresAltKey?: boolean;
    buttonRef?: React.MutableRefObject<HTMLButtonElement | undefined>;
    onClick?: () => void;
    form?: string;
    hideShortcutKey?: boolean;
    fill?: boolean;
};

function ButtonV2({
    type = 'primary',
    label = '',
    size = 'base',
    leftIcon,
    rightIcon,
    isDisabled = false,
    disabledHint,
    tooltipPosition = 'right',
    isLoading = false,
    isSubmit = false,
    shortcutKey,
    buttonRef,
    requiresAltKey = false,
    onClick,
    form,
    hideShortcutKey = false,
    fill = false,
}: ButtonV2Props): JSX.Element {
    const handleOnClick = useCallback(
        function handleOnClick(): void {
            onClick && onClick();
        },
        [onClick]
    );

    const detectHotKey = useCallback(
        (e: KeyboardEvent) => {
            const keyLookups: { [key: string]: string } = {
                BracketLeft: 'key[',
                BracketRight: 'key]',
                Comma: 'key,',
                Period: 'key.',
                ArrowLeft: 'keyarrowleft',
                ArrowRight: 'keyarrowright',
                Enter: 'keyenter',
            };

            const _eventKey = keyLookups[e.code] || e.code?.toLowerCase();

            if (
                _eventKey &&
                _eventKey === `key${shortcutKey.toLowerCase()}` &&
                !['[object HTMLTextAreaElement]', '[object HTMLInputElement]'].includes(e.target.toString())
            ) {
                if (requiresAltKey) {
                    if (e.altKey) {
                        e.preventDefault();
                        handleOnClick();
                    }
                } else {
                    handleOnClick();
                }
            }
        },

        [shortcutKey, requiresAltKey, handleOnClick]
    );

    useEffect(() => {
        if (shortcutKey && !isDisabled) {
            document.addEventListener('keydown', detectHotKey);
            return (): void => document.removeEventListener('keydown', detectHotKey);
        }
    }, [detectHotKey, isDisabled, shortcutKey]);

    return (
        <TooltipV2 message={disabledHint} disabled={!(isDisabled && disabledHint)} position={tooltipPosition}>
            <button
                form={form}
                ref={buttonRef}
                onClick={handleOnClick}
                type={isSubmit ? 'submit' : 'button'}
                className={`shrink-0 rounded-md ${styles.base} ${fill ? 'w-full' : ''}`}
                disabled={isDisabled}
            >
                <span
                    className={`flex ${fill ? 'justify-between' : 'justify-center'} items-center group gap-x-2 rounded-md ${
                        styles.type[type]
                    } ${styles.size[size]}`}
                >
                    {isLoading && <Spinner size={size === 'lg' || size === 'xl' ? 'sm' : 'xs'} />}
                    {leftIcon && <span className={`${styles.iconDimension[size]} ${styles.iconColour[type]}`}>{leftIcon}</span>}
                    {label && <p className="leading-none">{label}</p>}
                    {rightIcon && <span className={`${styles.iconDimension[size]} ${styles.iconColour[type]}`}>{rightIcon}</span>}
                    {shortcutKey && !hideShortcutKey && (
                        <kbd
                            className={`font-medium px-1 py-0.5 rounded-[5px] font-sans leading-none ${styles.shortcutKey[type]} ${styles.shortcutKeySize[size]}`}
                        >
                            {requiresAltKey && '⌥ '} {shortcutKey.toUpperCase() === 'ENTER' ? '\u23CE' : shortcutKey.toUpperCase()}
                        </kbd>
                    )}
                </span>
            </button>
        </TooltipV2>
    );
}

export default ButtonV2;
